"use strict";

exports.__esModule = true;
exports.LinearCreative = void 0;
var _Creative2 = require("./Creative");
var _ConversionUtils = require("../Utils/ConversionUtils");
var _VideoClicks = require("./VideoClicks");
var _YoLog = require("../Utils/YoLog");
var _TrackingReport = require("./TrackingReport");
var _CreativeUtils = require("../Utils/CreativeUtils");
var _StringUtils = require("../Utils/StringUtils");
function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
/**
 * Represents a linear creative object.
 *
 * Encapsulates the data from the `<Linear>` element of a VAST document and represents the actual
 * video content of an advert.<br/>
 * The creative may contain one or more industry icons that can be overlaid on top of the video
 * to provide some extended functionality for the advert.
 *
 * <b>NOTE:</b> The class implements CreativeEventHandler so that it can raise `ClickTracking` and analytic
 * events on behalf of a client. It supports the following event strings:
 * - `customClick`
 * 
 *  @hideconstructor
  * @see {@link Creative}, which this class extends
*/
var LinearCreative = /*#__PURE__*/function (_Creative) {
  _inheritsLoose(LinearCreative, _Creative);
  function LinearCreative(creativeData, videoClicks, interactiveCreative, broker) {
    var _this;
    _this = _Creative.call(this, creativeData) || this;
    /** @private */
    _this.duration = (0, _ConversionUtils.timeStringToMillis)(creativeData.duration);
    /** @private */
    _this.trackingMap = creativeData.trackingMap ? creativeData.trackingMap : new Map();
    /** @private */
    _this.videoClicks = videoClicks ? videoClicks : new _VideoClicks.VideoClicks("", null, null);
    /** @private */
    _this.icons = creativeData.icons;
    /** @private */
    _this.assetUri = creativeData.currentAssetUri ? creativeData.currentAssetUri : "";
    /** @private */
    _this.interactiveCreative = interactiveCreative;
    /** @private */
    _this.skipOffset = creativeData.skipOffset;
    /** @private */
    _this.broker = broker;
    return _this;
  }
  var _proto = LinearCreative.prototype;
  _proto.getAssetUri = function getAssetUri() {
    return this.assetUri;
  }

  /**
   * @return {string} the clickthrough URL of the linear creative portion of this Advert as a `string`
   */;
  _proto.getClickThroughUrl = function getClickThroughUrl() {
    return this.videoClicks.getClickThroughUrl();
  };
  _proto.getClickTrackingUrls = function getClickTrackingUrls() {
    return this.videoClicks.getClickTrackingUrls();
  }

  /**
   * @return {string[]} a string array of custom click URLs associated with the LinearCreative, which may be empty
   */;
  _proto.getCustomClickUrls = function getCustomClickUrls() {
    return this.videoClicks.getCustomClicks();
  };
  _proto.getDuration = function getDuration() {
    return this.duration;
  };
  _proto.getIndustryIcons = function getIndustryIcons() {
    return Object.assign([], this.icons);
  };
  _proto.getInteractiveCreative = function getInteractiveCreative() {
    return this.interactiveCreative;
  };
  _proto.getSkipOffset = function getSkipOffset() {
    if (!this.skipOffset || this.skipOffset === "") {
      return -1;
    }
    if (this.skipOffset.indexOf("%") !== -1) {
      var val = parseFloat(this.skipOffset.substring(0, this.skipOffset.length - 1));
      if (val) {
        return Math.round(val * this.duration / 100.0);
      } else {
        _YoLog.YoLog.w("Invalid skipoffset string: " + this.skipOffset);
        return -1;
      }
    } else {
      return (0, _ConversionUtils.timeStringToMillis)(this.skipOffset, -1);
    }
  };
  _proto.getTrackingMap = function getTrackingMap() {
    return this.trackingMap;
  };
  _proto.getTrackingReport = function getTrackingReport(type) {
    if (!this.trackingMap.has(type)) {
      return null;
    }
    return this.trackingMap.get(type);
  };
  _proto.initialiseInteractiveCreative = function initialiseInteractiveCreative(nonlinearCreatives) {
    if (this.interactiveCreative) {
      this.interactiveCreative.initialise(this.getAdParameters(), this.duration, nonlinearCreatives);
    }
  };
  _proto.isNonLinear = function isNonLinear() {
    return false;
  };
  _proto.isValid = function isValid() {
    return this.duration > 0;
  };
  _proto.onClickThrough = function onClickThrough() {
    var urls = this.videoClicks.getClickTrackingUrls();
    if (urls.length > 0) {
      var report = (0, _CreativeUtils.getClickTrackingReport)(urls, "ClickTracking");
      this.broker.fireTrackingReportForCreative(report, this);
    }

    // SDK-811: required for OMID
    this.broker.raiseTrackingCallbackForCreative("ClickTracking", this);
  };
  _proto.onTrackingEvent = function onTrackingEvent(event) {
    if (event === "customClick") {
      var customClicks = this.videoClicks.getCustomClicks();
      if (customClicks.length) {
        var report = new _TrackingReport.TrackingReport(event, customClicks);
        this.broker.fireTrackingReportForCreative(report, this);
      }
    } else {
      if (_TrackingReport.TrackingReport.isValidPlayerOperationEvent(event)) {
        _YoLog.YoLog.w("Use PlaybackEventHandler functions to signal event " + event);
        return;
      }
      if (_TrackingReport.TrackingReport.isTimelineEvent(event)) {
        _YoLog.YoLog.w("Invalid event: " + event + ". Timeline events are signalled by position updates");
        return;
      }
      var _report = this.trackingMap.get(event);
      if (_report) {
        this.broker.fireTrackingReportForCreative(_report, this);
      }
    }
  };
  _proto.setDuration = function setDuration(duration) {
    this.duration = duration;
  };
  _proto.toString = function toString() {
    var s = "";
    if (this.trackingMap.size > 0) {
      s += "\n ** Tracking events - ";
      for (var _iterator = _createForOfIteratorHelperLoose(this.trackingMap), _step; !(_step = _iterator()).done;) {
        var _step$value = _step.value,
          key = _step$value[0],
          value = _step$value[1];
        s += (0, _StringUtils.toIndentedString)("\n " + key + ":" + value);
      }
    }
    var iconString = "";
    if (this.icons.length > 0) {
      iconString += "\n  ** Industry Icon(s) - ";
      for (var _iterator2 = _createForOfIteratorHelperLoose(this.icons), _step2; !(_step2 = _iterator2()).done;) {
        var icon = _step2.value;
        iconString += icon;
      }
    }
    var linearString = "\n--- Linear Creative:\n - assetUri:";
    linearString += this.assetUri;
    if (this.interactiveCreative) {
      linearString += this.interactiveCreative;
    }
    linearString += _Creative.prototype.toString.call(this);
    if (!(0, _StringUtils.isNullOrEmpty)(this.skipOffset)) {
      linearString += "\n - SkipOffset:";
      linearString += this.skipOffset;
    }
    linearString += s;
    linearString += (0, _StringUtils.toIndentedString)(this.videoClicks);
    linearString += iconString;
    return linearString;
  };
  return LinearCreative;
}(_Creative2.Creative);
exports.LinearCreative = LinearCreative;